import React from 'react'
import { useNavigate, useLocation } from '@/lib/router-dom'
import clsx from 'clsx'
import PropTypes from 'prop-types'

export const NavigationLink = ({ to, icon, text, disabled, hidden, active }) => {
  const { goTo } = useNavigate()
  const { currentPath } = useLocation()

  const onClick = e => {
    e.preventDefault()
    if (!disabled) {
      goTo(to)
    }
  }

  // The path is the usual answer, but not every page that belongs to a link
  // sits under its URL — a submittals data element owns the detail pages too,
  // and they do not name it. Callers that know better say so; `??` rather than
  // `||` so an explicit false is honoured instead of falling through, which is
  // what every unselected sidebar sub-item passes.
  const isActive = active ?? currentPath.startsWith(to)

  const navigationClass = clsx(
    'navigation-item flex items-center gap-y-3.25 gap-x-2.5 relative w-full cursor-pointer no-underline type-body-1 text-grey-50 hover:text-white hover:bg-theme-50',
    isActive && 'navigation-item--active text-theme-50 bg-theme-95',
    disabled && 'navigation-item--disabled'
  )

  if (hidden) return null

  return (
    // The id is derived rather than the one hardcoded value every link used to
    // share: the sidebar now draws a row per data element, so a fixed id would
    // repeat across the page.
    <a href={to} onClick={onClick} className={navigationClass} id={`navigation-item-${to}`}>
      {/* min-h + self-stretch rather than a fixed h-12: a sub-item label long
          enough to wrap makes the row taller, and a fixed bar would sit centred
          against it instead of marking the whole row. Single-line rows are 48px
          either way. */}
      <div className="w-1.25 min-h-12 self-stretch [.navigation-item--active_&]:bg-theme-50" />
      <div className="flex justify-center w-6">{icon}</div>
      <div className="font-avenir relative top-px ml-2.5 flex-1 [.navigation-item--active_&]:font-avenir-heavy">
        {text}
      </div>
    </a>
  )
}

NavigationLink.propTypes = {
  to: PropTypes.string.isRequired,
  // Optional: a sidebar sub-item passes none, and the empty slot the row draws
  // for it is what indents the label under its group's.
  icon: PropTypes.node,
  text: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  active: PropTypes.bool,
}
